import { useRef, useState, useMemo, useCallback } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Switch,
  Tag,
  TagLabel,
  Wrap,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'

export default function ContainerYardPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess } = useToastNotification()

  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalInactive, setTotalInactive] = useState(0)
  const [updatingStatus, setUpdatingStatus] = useState<Record<number, boolean>>({})

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: {
          name: 'Name',
          withSort: true,
          maxWidth: '200px',
        },
        short_name: {
          name: 'Short Name',
          maxWidth: '80px',
        },
        address: {
          name: 'Location',
          withSort: true,
        },
        contact_name: {
          name: 'Contact Person',
          maxWidth: '150px',
        },
        contact_mobile: {
          name: 'Mobile Number',
          maxWidth: '180px',
        },
        landlines: {
          name: 'Landline/s',
          maxWidth: '180px',
        },
        location_type: {
          name: 'Type',
          maxWidth: '150px',
        },
        is_active: {
          name: 'Status',
          maxWidth: '100px',
        },
      },
      dataSource: '/container-yards/yard-list',
      // Use API endpoint as data source, but route `/container-yards/:id` for Edit
      editPath: '/container-yards',
      primaryKey: 'id',
      softDelete: false,
      displayInModal: false,
      noRecordText: 'No container yard found',
      otherActions: undefined,
      showCheckboxes: false,
    }),
    []
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: false,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access]
  )

  const showSubSubCallback = useMemo(
    () => (active: number, inactive: number) => {
      setTotalRows(active)
      setTotalInactive(inactive)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleSearch = async () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleStatusToggle = useCallback(async (yardId: number, currentStatus: number) => {
    setUpdatingStatus((prev) => ({ ...prev, [yardId]: true }))
    
    try {
      if (currentStatus === 1) {
        await apiClient.patch(`/container-yards/deactivate-yard/${yardId}`)
      } else if (currentStatus === 0) {
        await apiClient.patch(`/container-yards/activate-yard/${yardId}`)
      }
      
      showSuccess('Success', 'Status updated successfully')
      tableRef.current?.reload()
    } catch (error: any) {
      showError(error)
    } finally {
      setUpdatingStatus((prev => {
        const updated = { ...prev }
        delete updated[yardId]
        return updated
      }))
    }
  }, [showSuccess, showError])

  const customRenderers = useMemo(
    () => ({
      is_active: (value: any, row: any) => {
        const statusValue = typeof value === 'number' ? value : parseInt(value) || 0
        const isUpdating = updatingStatus[row.id] || false
        
        return (
          <Switch
            isChecked={statusValue === 1}
            onChange={() => handleStatusToggle(row.id, statusValue)}
            colorScheme="blue"
            isDisabled={isUpdating}
          />
        )
      },
      landlines: (value: any) => {
        if (!value) return <span></span>
        
        // Handle both string (comma-separated) and array formats
        const landlineArray = Array.isArray(value)
          ? value
          : typeof value === 'string'
          ? value.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0)
          : []
        
        if (landlineArray.length === 0) return <span></span>
        
        return (
          <Wrap spacing={1}>
            {landlineArray.map((landline: string, index: number) => (
              <Tag
                key={index}
                size="sm"
                borderRadius="2px"
                variant="subtle"
                colorScheme="cyan"
                px={2}
                py={1}
              >
                <TagLabel>{landline}</TagLabel>
              </Tag>
            ))}
          </Wrap>
        )
      },
      name: (value: any) => {
        return (
          <Box
            maxW="200px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      address: (value: any) => {
        return (
          <Box
            maxW="200px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      contact_name: (value: any) => {
        return (
          <Box
            maxW="150px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
    }),
    [updatingStatus, handleStatusToggle]
  )

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Container Yard List
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage container yards and their details
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={5} width="100%">
                  <Box flex="0 1 auto" minW="400px" maxW="400px">
                    <InputGroup size="sm">
                      <Input
                        ref={searchRef}
                        placeholder="Search by name or location..."
                        onKeyDown={handleKeyDown}
                        defaultValue={searchQuery}
                        pr="2.5rem"
                      />
                      <InputRightElement width="2.5rem">
                        <IconButton
                          aria-label="Search"
                          icon={<Search size={16} />}
                          size="sm"
                          h="1.75rem"
                          w="1.75rem"
                          onClick={handleSearch}
                        variant="ghost"
                        />
                      </InputRightElement>
                    </InputGroup>
                  </Box>

                  <Box>
                    <ActionButtons
                      pathname="/container-yards"
                      createLabel="Add Container Yard"
                    />
                  </Box>
                </HStack>
                <Box mx={4} height={4} />
              </Box>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/container-yards/${id}`)}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
